'use client';

import { c as _c } from "react/compiler-runtime";
import { useMandatoryContext } from '@repo/hooks';
import { useRouter, useSearchParams } from 'next/navigation';
import { createContext, lazy, type PropsWithChildren, useCallback, useMemo } from 'react';
import type { Dialog } from '@/helpers/dialog';
import { consentLocalStorageKey, ConsentType } from '@/helpers/consent';
import { NAVIGATION_VALUE_MARKETS, NAVIGATION_VALUE_VAULTS } from '@/helpers/routing';
import { useNavigationOptions } from '@/hooks/useNavigationOptions';
import { useTermsOfUseAccepted } from '../../TermsOfUse/TermsOfUse.context';
import { useWeb3Context } from '../../Web3Context';
const EarnDialogContentClient = lazy(() => import('@/components/common/Dialog/EarnDialog/EarnDialogContentClient'));
const BorrowDialogContentClient = lazy(() => import('@/components/common/Dialog/BorrowDialog/BorrowDialogContentClient'));
const LiteDeprecationDialog = lazy(() => import('@/components/common/Dialog/LiteDeprecationDialog/LiteDeprecationDialog'));
export interface DialogOnMountDataContextProps {
  dialogs: Dialog[];
}
const DialogOnMountDataContext = createContext<DialogOnMountDataContextProps | null>(null);
export const useDialogOnMountDataContext = () => {
  return useMandatoryContext(DialogOnMountDataContext, "DialogOnMountContextData");
};
export type DialogOnMountDataProviderProps = PropsWithChildren;
const DialogOnMountContextDataProvider = t0 => {
  const $ = _c(29);
  const {
    children
  } = t0;
  const {
    account
  } = useWeb3Context();
  const termsAccepted = useTermsOfUseAccepted();
  const {
    navigationValue
  } = useNavigationOptions();
  const router = useRouter();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("from");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isFromLite = t1 === "lite";
  let t2;
  if ($[2] !== router) {
    t2 = () => {
      const params = new URLSearchParams(window.location.search);
      if (!params.has("from")) {
        return;
      }
      params.delete("from");
      const query = params.toString();
      router.replace(query ? `${window.location.pathname}?${query}` : window.location.pathname);
    };
    $[2] = router;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const clearFromLiteParam = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LiteDeprecationDialog />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = termsAccepted && isFromLite;
  let t5;
  if ($[5] !== clearFromLiteParam) {
    t5 = {
      closable: true,
      maxWidth: "480px",
      onClose: clearFromLiteParam
    };
    $[5] = clearFromLiteParam;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = {
      component: t3,
      condition: t4,
      store: "local",
      dialogOptions: t5
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <EarnDialogContentClient />;
    t8 = consentLocalStorageKey(ConsentType.Earn);
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  const t9 = termsAccepted && account.isConnected && navigationValue === NAVIGATION_VALUE_VAULTS;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      fullSize: true,
      closable: false
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t9) {
    t11 = {
      component: t7,
      keyStore: t8,
      condition: t9,
      store: "local",
      dialogOptions: t10
    };
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <BorrowDialogContentClient />;
    t13 = consentLocalStorageKey(ConsentType.Borrow);
    $[15] = t12;
    $[16] = t13;
  } else {
    t12 = $[15];
    t13 = $[16];
  }
  const t14 = termsAccepted && account.isConnected && navigationValue === NAVIGATION_VALUE_MARKETS;
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      fullSize: true,
      closable: false
    };
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== t14) {
    t16 = {
      component: t12,
      keyStore: t13,
      condition: t14,
      store: "local",
      dialogOptions: t15
    };
    $[18] = t14;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== t11 || $[21] !== t16 || $[22] !== t6) {
    t17 = [t6, t11, t16];
    $[20] = t11;
    $[21] = t16;
    $[22] = t6;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  const dialogs = t17;
  let t18;
  if ($[24] !== dialogs) {
    t18 = {
      dialogs
    };
    $[24] = dialogs;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  const value = t18;
  let t19;
  if ($[26] !== children || $[27] !== value) {
    t19 = <DialogOnMountDataContext.Provider value={value}>{children}</DialogOnMountDataContext.Provider>;
    $[26] = children;
    $[27] = value;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  return t19;
};
export default DialogOnMountContextDataProvider;